// services/api.js
import { BASE_PATH } from '../config/paths';

// 配置默认基础URL - 从环境变量获取或使用默认值
// 确保 URL 末尾没有斜杠，避免双斜杠问题
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.example.com').replace(/\/$/, '');

// Helper functions
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'API request failed');
  }
  return data;
};

// Auth Service 
export const authAPI = {
  login: async (username, password) => {
    try {
      // 使用fetch替代axios
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  getCurrentUser: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get user info');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },

  createUser: async (userData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }
};

// Contact Service
export const contactService = {
  // 保留原有方法
  submitContact: async (contactData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit contact form');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  },
  
  // 添加兼容旧代码的方法名
  submitContactForm: async (formData) => {
    try {
      console.log('提交联系表单数据:', JSON.stringify(formData, null, 2));
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          category: formData.category,
          notifications: {
            user: {
              subject: 'Thank you for contacting Game Mixer',
              template: 'contact-confirmation',
              data: {
                name: formData.name,
                email: formData.email,
                message: formData.message,
                category: `**${formData.category}**`,
                baseUrl: BASE_PATH
              }
            },
            admin: {
              subject: 'New Contact Form Submission',
              template: 'admin-notification',
              data: {
                name: formData.name,
                email: formData.email,
                message: formData.message,
                category: formData.category,
                submitTime: new Date().toISOString(),
                baseUrl: BASE_PATH
              }
            }
          }
        })
      });

      if (!response.ok) {
        let errorMsg = 'Failed to submit contact form';
        try {
          const error = await response.json();
          errorMsg = error.message || errorMsg;
        } catch (parseError) {
          console.error('解析错误响应失败:', parseError);
          errorMsg = `${errorMsg} (Status: ${response.status})`;
        }
        throw new Error(errorMsg);
      }

      return response.json();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  }
};

// Donation Service
export const donationService = {
  // 更新旧方法，添加必需字段
  submitMonetaryDonation: async (donationData) => {
    try {
      console.log('提交货币捐赠数据:', JSON.stringify(donationData, null, 2));
      
      // 构建请求体，包含服务器期望的所有字段
      const requestBody = {
        amount: donationData.amount,
        contactEmail: donationData.email, // 注意：服务器期望 contactEmail 而不是 email
        phoneNumber: donationData.phoneNumber || null,
        name: donationData.name,
        paymentMethod: donationData.paymentMethod,
        // 添加必需的通知配置
        notifications: {
          donor: {
            subject: 'Thank you for your donation to Game Mixer',
            template: 'donation-confirmation',
            data: {
              name: donationData.name,
              amount: donationData.amount,
              paymentMethod: donationData.paymentMethod,
              baseUrl: BASE_PATH
            }
          },
          admin: {
            subject: 'New Monetary Donation Received',
            template: 'admin-donation-notification',
            data: {
              donorEmail: donationData.email,
              phoneNumber: donationData.phoneNumber || null,
              amount: donationData.amount,
              paymentMethod: donationData.paymentMethod,
              submitTime: new Date().toISOString(),
              baseUrl: BASE_PATH
            }
          }
        }
      };
      
      console.log('发送请求体:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/donations/monetary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        let errorMsg = 'Failed to submit donation';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (parseError) {
          console.error('解析错误响应失败:', parseError);
          errorMsg = `${errorMsg} (Status: ${response.status})`;
        }
        throw new Error(errorMsg);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error submitting monetary donation:', error);
      throw error;
    }
  },

  submitGoodsDonation: async (donationData) => {
    try {
      console.log('提交物品捐赠数据:', JSON.stringify(donationData, null, 2));
      
      // 构建请求体，包含服务器期望的所有字段
      const requestBody = {
        donationType: donationData.donationType,
        details: donationData.details,
        contactEmail: donationData.email, // 注意：服务器期望 contactEmail 而不是 email
        phoneNumber: donationData.phoneNumber || null,
        name: donationData.name,
        // 添加必需的通知配置
        notifications: {
          donor: {
            subject: 'Thank you for your goods donation to Game Mixer',
            template: 'goods-donation-confirmation',
            data: {
              name: donationData.name,
              donationType: donationData.donationType,
              details: donationData.details,
              baseUrl: BASE_PATH
            }
          },
          admin: {
            subject: 'New Goods Donation Offer Received',
            template: 'admin-goods-notification',
            data: {
              donorEmail: donationData.email,
              phoneNumber: donationData.phoneNumber || null,
              donationType: donationData.donationType,
              details: donationData.details,
              submitTime: new Date().toISOString(),
              baseUrl: BASE_PATH
            }
          }
        }
      };
      
      console.log('发送请求体:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/donations/goods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        let errorMsg = 'Failed to submit donation';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (parseError) {
          console.error('解析错误响应失败:', parseError);
          errorMsg = `${errorMsg} (Status: ${response.status})`;
        }
        throw new Error(errorMsg);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error submitting goods donation:', error);
      throw error;
    }
  }
};

// Event Service
export const eventService = {
  // Get all events with optional tag filter
  getAllEvents: async (tag = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/events${tag ? `?tag=${tag}` : ''}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch events');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  // Get event by ID
  getEventById: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${id}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch event');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching event with ID ${id}:`, error);
      throw error;
    }
  },

  // Get all available event tags
  getEventTags: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/tags`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch tags');
      }
      
      const data = await response.json();
      return data.tags;
    } catch (error) {
      console.error('Error fetching event tags:', error);
      throw error;
    }
  },

  // Create new event (requires admin authentication)
  createEvent: async (eventData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create event');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  // Update existing event (requires admin authentication)
  updateEvent: async (id, eventData, token) => {
    try {
      if (!id) {
        throw new Error('Event ID is required');
      }

      if (!token) {
        throw new Error('Authentication token is required');
      }

      console.log('Updating event with ID:', id);
      console.log('Update payload:', eventData);
      
      const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update event');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating event with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete event (requires admin authentication)
  deleteEvent: async (id, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete event');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error deleting event with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Get pre-signed URL for uploading
  getImageUploadUrl: async (fileInfo) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE_URL}/upload/get-presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(fileInfo)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get upload URL');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting upload URL:', error);
      throw error;
    }
  }
};

// Image Service
export const imageService = {
  // Get upload signature for direct Cloudinary uploads
  getUploadSignature: async (folder = 'events') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/images/signature?folder=${folder}`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get upload signature');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting upload signature:', error);
      throw error;
    }
  },

  // Upload image via backend
  uploadImage: async (file, options = {}) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Validate file input
      if (!file || !(file instanceof File || file instanceof Blob)) {
        throw new Error('Invalid file object provided for upload');
      }
      
      // Try direct upload to Cloudinary first
      try {
        // Get signature
        const signatureData = await imageService.getUploadSignature(options.folder || 'events');
        
        // Create FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', signatureData.apiKey);
        formData.append('timestamp', signatureData.timestamp);
        formData.append('signature', signatureData.signature);
        formData.append('folder', signatureData.folder);
        
        // Add public ID if available
        if (options.publicId) {
          formData.append('public_id', options.publicId);
        }
        
        // Upload to Cloudinary
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );
        
        if (!response.ok) {
          throw new Error('Direct upload failed, falling back to backend upload');
        }
        
        return await response.json();
      } catch (directUploadError) {
        console.warn('Direct upload failed, using backend upload:', directUploadError);
        
        // Convert file to base64 for backend upload
        const fileToBase64 = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              resolve(reader.result);
            };
            reader.onerror = (error) => {
              reject(error);
            };
          });
        };
        
        const base64Data = await fileToBase64(file);
        const folder = options.folder || 'events';
        
        // URL 已在 API_BASE_URL 声明时处理了尾部斜杠
        const baseUrl = API_BASE_URL;
        
        const url = `${baseUrl}/images/upload?folder=${folder}`;
        console.log('Upload URL:', url);
        console.log('File type:', file.type, 'size:', Math.round(file.size / 1024) + 'KB');
        
        // Send as JSON with base64 data instead of FormData
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            image: base64Data
          })
        });
        
        // Detailed error handling
        if (!response.ok) {
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Server error: ${response.status} ${response.statusText}`;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          
          console.error('Upload failed with status:', response.status);
          throw new Error(errorMessage);
        }
        
        const result = await response.json();
        console.log('Upload successful:', result);
        return result;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Delete image from Cloudinary (requires admin authentication)
  deleteImage: async (publicId) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication required to delete images');
      }
      
      const response = await fetch(`${API_BASE_URL}/images/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ publicId })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete image');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  // Upload image directly to Cloudinary using a pre-signed signature
  uploadToCloudinaryDirect: async (file, options = {}) => {
    try {
      // Get signature for direct upload
      const signatureData = await imageService.getUploadSignature(options.folder || 'events');
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', signatureData.apiKey);
      formData.append('timestamp', signatureData.timestamp);
      formData.append('signature', signatureData.signature);
      formData.append('folder', signatureData.folder);
      
      // Upload directly to Cloudinary
      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.message || 'Failed to upload to Cloudinary');
      }
      
      const data = await uploadResponse.json();
      return {
        url: data.secure_url,
        publicId: data.public_id,
        width: data.width,
        height: data.height
      };
    } catch (error) {
      console.error('Error uploading directly to Cloudinary:', error);
      throw error;
    }
  }
};